import { IFeatureStatus, IFeatureType } from '../interface/featureQuery';
import type { MimeType } from '../interface/mimeType';

const PROMISE_SUPPORTED = Promise.resolve(IFeatureStatus.FeatureStatusSupported);
const PROMISE_UNSUPPORTED = Promise.resolve(IFeatureStatus.FeatureStatusUnsupported);

const MAP_FEATURE: Record<IFeatureType, () => Promise<IFeatureStatus>> = {
    [IFeatureType.FeatureTypeUndef]: () => PROMISE_UNSUPPORTED,
    [IFeatureType.FeatureTypeSymbol]: () => {
        const globalObject = globalThis as Record<string, unknown>;
        return globalObject['Symbol'] !== undefined ? PROMISE_SUPPORTED : PROMISE_UNSUPPORTED;
    },
    [IFeatureType.FeatureTypeIndexedDB]: () => {
        const globalObject = globalThis as Record<string, unknown>;
        return ['indexedDB', 'mozIndexedDB', 'webkitIndexedDB', 'msIndexedDB'].some(
            featureString => featureString in globalObject,
        )
            ? PROMISE_SUPPORTED
            : PROMISE_UNSUPPORTED;
    },
    [IFeatureType.FeatureTypeModule]: () => {
        try {
            /* eslint-disable-next-line no-new-func, @typescript-eslint/no-implied-eval, no-new */
            new Function('import("")');
            return PROMISE_SUPPORTED;
        } catch {
            return PROMISE_UNSUPPORTED;
        }
    },
    [IFeatureType.FeatureTypeLastValue]: () => PROMISE_UNSUPPORTED,
};

const _canPlayMediaType = (mediaType: 'audio' | 'video', mimeType: MimeType): IFeatureStatus => {
    const object = document.createElement(mediaType);
    const status =
        object.canPlayType(mimeType) === 'probably' || object.canPlayType(mimeType) === 'maybe'
            ? IFeatureStatus.FeatureStatusSupported
            : IFeatureStatus.FeatureStatusUnsupported;
    return status;
};

const MAP_DUMMY_BASE64_DATA: Partial<Record<MimeType, string>> = {
    'image/webp': 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=',
    'image/avif':
        'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUEAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAABkAAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAEAAAABAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgSAAAAAAABNjb2xybmNseAABAA0ABoAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACFtZGF0EgAKBzgABpAQ0GkyDBYAAABIAAAAeUusgg==',
};

const _canDisplayImageType = (imageMimeType: MimeType): Promise<IFeatureStatus> =>
    new Promise((resolve, reject) => {
        const encodedData = MAP_DUMMY_BASE64_DATA[imageMimeType];

        if (encodedData === undefined) {
            resolve(IFeatureStatus.FeatureStatusUnsupported);
            return;
        }

        const image = new Image();
        image.src = encodedData;
        image.addEventListener('error', error => {
            reject(error);
        });
        image.addEventListener('load', () => {
            resolve(IFeatureStatus.FeatureStatusSupported);
        });
    });

const MAP_MIMETYPE_DEFAULTS: Partial<Record<MimeType, () => Promise<IFeatureStatus>>> = {
    /* HLS m3u8 files */
    'application/x-mpegURL': () => Promise.resolve(IFeatureStatus.FeatureStatusSupported),
    'vnd.apple.mpegURL': () => Promise.resolve(IFeatureStatus.FeatureStatusSupported),
};

/* Reduce the status with the lowest value, where the lowest value is lowest support level & bounds check between Undef<->LastValue */
const _reduceFeatureStatuses = (statuses: IFeatureStatus[]) =>
    Math.max(
        Math.min(...statuses, IFeatureStatus.FeatureStatusLastValue - 1),
        IFeatureStatus.FeatureStatusUndef + 1,
    );

const _defaultQuery = () => Promise.resolve(IFeatureStatus.FeatureStatusUnknown);

const CACHED_RESULTS_MIMETYPE: Record<string, IFeatureStatus> = {};
const CACHED_RESULTS_FEATURE: Partial<Record<IFeatureType, IFeatureStatus>> = {};

async function canSupportMediaMimeTypes(mimeTypes: MimeType[]): Promise<IFeatureStatus> {
    const textResolver = (mimeType: MimeType): Promise<IFeatureStatus> | undefined => {
        if (!mimeType.startsWith('text/')) {
            return undefined;
        }

        return Promise.resolve(IFeatureStatus.FeatureStatusSupported);
    };

    const imageResolver = (mimeType: MimeType): Promise<IFeatureStatus> | undefined => {
        if (!mimeType.startsWith('image/')) {
            return undefined;
        }

        switch (mimeType.toLowerCase()) {
            case 'image/bmp':
            case 'image/gif':
            case 'image/jpg':
            case 'image/jpeg':
            case 'image/tiff':
            case 'image/png':
            case 'image/svg':
            case 'image/svg+xml':
            case 'image/x-icon':
                return Promise.resolve(IFeatureStatus.FeatureStatusSupported);

            default:
                return _canDisplayImageType(mimeType);
        }
    };

    const avResolver = (mimeType: MimeType): Promise<IFeatureStatus> | undefined => {
        if (mimeType.toLowerCase().startsWith('audio')) {
            return Promise.resolve(_canPlayMediaType('audio', mimeType));
        } else if (mimeType.toLowerCase().startsWith('video')) {
            return Promise.resolve(_canPlayMediaType('video', mimeType));
        } else {
            return undefined;
        }
    };

    const statuses = await Promise.all(
        mimeTypes.map(
            mimeType =>
                CACHED_RESULTS_MIMETYPE[mimeType] ??
                textResolver(mimeType) ??
                imageResolver(mimeType) ??
                avResolver(mimeType) ??
                MAP_MIMETYPE_DEFAULTS[mimeType]?.() ??
                _defaultQuery(),
        ),
    );

    const result = _reduceFeatureStatuses(statuses);

    for (const [index, mimeType] of mimeTypes.entries()) {
        const status = statuses[index];
        if (status === undefined) {
            throw new Error(
                `Error while caching supported mime types. Couldn't find status for mime type '${mimeType}'`,
            );
        } else {
            CACHED_RESULTS_MIMETYPE[mimeType] = status;
        }
    }

    return result;
}

async function canSupportFeatures(features: IFeatureType[]): Promise<IFeatureStatus> {
    const statuses = await Promise.all(
        features.map(
            feature => CACHED_RESULTS_FEATURE[feature] ?? MAP_FEATURE[feature]() ?? _defaultQuery,
        ),
    );

    const result = _reduceFeatureStatuses(statuses);

    for (const [index, feature] of features.entries()) {
        CACHED_RESULTS_FEATURE[feature] = statuses[index];
    }

    return result;
}

export default Object.freeze({
    mediaSupported: canSupportMediaMimeTypes,
    featuresSupported: canSupportFeatures,
});
